import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload, FileUploadSelectEvent } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  appConfirmDialogClasses,
  appInnerCardStyle,
  appMutedTextStyle,
  renderConfirmMessage,
} from "../../../components/general/uiSurfaceStyles";
import {
  combineLosAngelesDateAndTime,
  formatLosAngelesDateTimeLocalInput,
  getLosAngelesCalendarValue,
  getLosAngelesDateKey,
  getLosAngelesDateKeyFromCalendarValue,
  getLosAngelesTimeKey,
  LOS_ANGELES_TIME_OPTIONS,
  updateLosAngelesDateTimePart,
} from "../../../utils/losAngelesDateTime";

type EntryMode = "incident" | "daily_note" | "resident_movement";

type ResidentEntryLogPanelProps = {
  entryType: EntryMode;
  entries: any[];
  loading?: boolean;
  saving?: boolean;
  canEditEntry?: (entry: any) => boolean;
  onCreateEntry: (payload: Record<string, any>) => Promise<void>;
  onUpdateEntry: (entryLogId: string, payload: Record<string, any>) => Promise<void>;
  onDeleteDraft?: (entryLogId: string) => Promise<void>;
  onFormatText?: (request: {
    text?: string;
    taskType: string;
    tone: string;
    domain: string;
    outputStyle: string;
    metadata?: Record<string, any>;
  }) => Promise<{ formattedText?: string } | null | undefined>;
  defaultComposerVisible?: boolean;
  showComposerToggleButton?: boolean;
  composerVisible?: boolean;
  onComposerVisibleChange?: (visible: boolean) => void;
  hideEntriesSection?: boolean;
  defaultEntriesVisible?: boolean;
  entriesVisible?: boolean;
  onEntriesVisibleChange?: (visible: boolean) => void;
  showEntriesToggleButton?: boolean;
  serverEntriesPage?: number;
  serverEntriesHasPreviousPage?: boolean;
  serverEntriesHasNextPage?: boolean;
  onEntriesPreviousPage?: () => void;
  onEntriesNextPage?: () => void;
  enableImages?: boolean;
  onUploadImages?: (files: File[]) => Promise<Array<{ key: string; fileName: string; url: string }>>;
  showDraftsSection?: boolean;
  allowDrafts?: boolean;
  onDraftSaved?: () => void;
  sameFacilityResidentOptions?: Array<{ residentId: string; residentName: string }>;
  readOnly?: boolean;
};

type FormState = {
  title: string;
  occurredAt: string;
  incidentType: string;
  details: string;
  actionTaken: string;
  notificationsMade: string;
  notes: string;
  shiftLabel: string;
  tagsInput: string;
  involvedResidentIds: string[];
};

type FormErrors = Partial<Record<keyof FormState, string>>;

type AttachedImage = {
  key?: string;
  fileName: string;
  url: string;
  file?: File;
};

const INCIDENT_TYPE_OPTIONS = [
  { label: "Select incident type", value: "" },
  { label: "Fall", value: "fall" },
  { label: "Injury", value: "injury" },
  { label: "Behavior Event", value: "behavior_event" },
  { label: "Resident-to-Resident Altercation", value: "resident_to_resident_altercation" },
  { label: "Resident-to-Staff Altercation", value: "resident_to_staff_altercation" },
  { label: "Medication Issue", value: "medication_issue" },
  { label: "Missed Medication", value: "missed_medication" },
  { label: "Medication Reaction", value: "medication_reaction" },
  { label: "Elopement Risk", value: "elopement_risk" },
  { label: "Wandering or Exit Seeking", value: "wandering_or_exit_seeking" },
  { label: "Unsafe Transfer or Mobility Issue", value: "unsafe_transfer_or_mobility_issue" },
  { label: "Skin Issue or Pressure Injury", value: "skin_issue_or_pressure_injury" },
  { label: "Choking or Swallowing Issue", value: "choking_or_swallowing_issue" },
  { label: "Property Damage", value: "property_damage" },
  { label: "Visitor or Family Incident", value: "visitor_or_family_incident" },
  { label: "Equipment or Device Issue", value: "equipment_or_device_issue" },
  { label: "Environmental Safety Issue", value: "environmental_safety_issue" },
  { label: "Hospital Transfer", value: "hospital_transfer" },
  { label: "911 or Emergency Services", value: "911_or_emergency_services" },
  { label: "Emergency", value: "emergency" },
  { label: "Abuse or Neglect Concern", value: "abuse_or_neglect_concern" },
  { label: "Other", value: "other" },
];

const MOVEMENT_TYPE_OPTIONS = [
  { label: "Select entry type", value: "" },
  { label: "In", value: "in" },
  { label: "Out", value: "out" },
];

const AI_POLISH_MAX_WORDS = 2000;
const MAX_INCIDENT_IMAGES = 5;
const QUICK_TIME_MINUTES = [0, 30, 60, 120, 300, 600];
const QUICK_TIME_MATCH_TOLERANCE_MINUTES = 2;

const formatDateTimeLocalInput = (value?: string) => {
  return formatLosAngelesDateTimeLocalInput(value);
};

const toIsoString = (value: string) => {
  if (!value) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    return combineLosAngelesDateAndTime(getLosAngelesDateKey(value), getLosAngelesTimeKey(value));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const formatTimestamp = (value?: string) => {
  if (!value) {
    return "Not set";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
};

const getResidentEntryDisplayStaffName = (entry: any) =>
  String(entry?.sourceStaffName || "").trim() || String(entry?.recordedBy || "").trim();

const getQuickTimeLabel = (minutesAgo: number) => {
  if (minutesAgo === 0) {
    return "Now";
  }
  if (minutesAgo % 60 === 0) {
    return `${minutesAgo / 60} hr`;
  }
  return `${minutesAgo} min`;
};

const countWords = (value: string) => String(value || "").trim().split(/\s+/).filter(Boolean).length;

const trimToWordLimit = (value: string, maxWords: number) => {
  const words = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) {
    return value;
  }
  return words.slice(0, maxWords).join(" ");
};

const getDefaultFormState = (): FormState => ({
  title: "",
  occurredAt: formatDateTimeLocalInput(),
  incidentType: "",
  details: "",
  actionTaken: "",
  notificationsMade: "",
  notes: "",
  shiftLabel: "",
  tagsInput: "",
  involvedResidentIds: [],
});

const getPanelLabels = (entryType: EntryMode) => {
  if (entryType === "incident") {
    return {
      sectionTitle: "Incident Report",
      entryLabel: "incident report",
      newButtonLabel: "New Incident Report",
      hideButtonLabel: "Hide Incident Report",
      draftHeader: "Saved Incident Drafts",
      recentHeader: "Recent Incident Reports",
      saveFinalLabel: "Save Incident Report",
      saveDraftLabel: "Save Draft",
      detailsLabel: "What happened",
      detailsPlaceholder: "Write what happened in a clear, professional note.",
      notesLabel: "Extra notes",
      statusFinalLabel: "submitted",
    };
  }

  if (entryType === "resident_movement") {
    return {
      sectionTitle: "Entry Tracker",
      entryLabel: "entry",
      newButtonLabel: "New Entry",
      hideButtonLabel: "Hide Entry Form",
      draftHeader: "Saved Entry Drafts",
      recentHeader: "Recent Entries",
      saveFinalLabel: "Save Entry",
      saveDraftLabel: "Save Draft",
      detailsLabel: "Note",
      detailsPlaceholder: "Optional note for this entry.",
      notesLabel: "Extra notes",
      statusFinalLabel: "saved",
    };
  }

  return {
    sectionTitle: "Progress Notes",
    entryLabel: "progress note",
    newButtonLabel: "New Progress Note",
    hideButtonLabel: "Hide Progress Note",
    draftHeader: "Saved Progress Note Drafts",
    recentHeader: "Recent Progress Notes",
    saveFinalLabel: "Save Progress Note",
    saveDraftLabel: "Save Draft",
    detailsLabel: "Progress note",
    detailsPlaceholder: "Write the main note for this shift or update.",
    notesLabel: "Extra notes",
    statusFinalLabel: "final",
  };
};

const ResidentEntryLogPanel = ({
  entryType,
  entries,
  loading = false,
  saving = false,
  canEditEntry,
  onCreateEntry,
  onUpdateEntry,
  onDeleteDraft,
  onFormatText,
  defaultComposerVisible = true,
  showComposerToggleButton = true,
  composerVisible,
  onComposerVisibleChange,
  hideEntriesSection = false,
  defaultEntriesVisible = true,
  entriesVisible,
  onEntriesVisibleChange,
  showEntriesToggleButton = true,
  serverEntriesPage = 1,
  serverEntriesHasPreviousPage = false,
  serverEntriesHasNextPage = false,
  onEntriesPreviousPage,
  onEntriesNextPage,
  enableImages = false,
  onUploadImages,
  showDraftsSection = true,
  allowDrafts = true,
  onDraftSaved,
  sameFacilityResidentOptions = [],
  readOnly = false,
}: ResidentEntryLogPanelProps) => {
  const labels = useMemo(() => getPanelLabels(entryType), [entryType]);
  const composerRef = useRef<HTMLDivElement | null>(null);
  const fileUploadRef = useRef<FileUpload | null>(null);
  const [showComposer, setShowComposer] = useState<boolean>(defaultComposerVisible);
  const [showEntries, setShowEntries] = useState<boolean>(defaultEntriesVisible);
  const [editingEntryId, setEditingEntryId] = useState<string>("");
  const [formState, setFormState] = useState<FormState>(getDefaultFormState());
  const [formattingField, setFormattingField] = useState<"" | "details" | "actionTaken" | "notificationsMade" | "notes">("");
  const [showQuickTimeOptions, setShowQuickTimeOptions] = useState<boolean>(true);
  const [showExactTimeInput, setShowExactTimeInput] = useState<boolean>(false);
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [previewImage, setPreviewImage] = useState<AttachedImage | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [generatingDailyNote, setGeneratingDailyNote] = useState<boolean>(false);
  const [generatingIncidentSample, setGeneratingIncidentSample] = useState<boolean>(false);
  const [submittingForm, setSubmittingForm] = useState<boolean>(false);
  const [showRecentReuseDialog, setShowRecentReuseDialog] = useState<boolean>(false);

  const isComposerControlled = typeof composerVisible === "boolean";
  const composerIsVisible = isComposerControlled ? Boolean(composerVisible) : showComposer;
  const isEntriesControlled = typeof entriesVisible === "boolean";
  const entriesAreVisible = isEntriesControlled ? Boolean(entriesVisible) : showEntries;

  useEffect(() => {
    if (!isComposerControlled) {
      setShowComposer(defaultComposerVisible);
    }
  }, [defaultComposerVisible, isComposerControlled]);

  useEffect(() => {
    if (!isEntriesControlled) {
      setShowEntries(defaultEntriesVisible);
    }
  }, [defaultEntriesVisible, isEntriesControlled]);

  const updateComposerVisibility = (visible: boolean) => {
    if (!isComposerControlled) {
      setShowComposer(visible);
    }
    onComposerVisibleChange?.(visible);
  };

  const updateEntriesVisibility = (visible: boolean) => {
    if (!isEntriesControlled) {
      setShowEntries(visible);
    }
    onEntriesVisibleChange?.(visible);
  };

  const draftEntries = useMemo(
    () => entries.filter((entry) => String(entry?.status || "").trim().toLowerCase() === "draft"),
    [entries],
  );

  const recentEntries = useMemo(
    () => entries.filter((entry) => String(entry?.status || "").trim().toLowerCase() !== "draft"),
    [entries],
  );
  const recentReuseEntries = useMemo(
    () => entryType === "daily_note" ? recentEntries.slice(0, 10) : [],
    [entryType, recentEntries],
  );
  const showRecentReuseSummaryCard = entryType === "daily_note" && recentReuseEntries.length > 0;

  const resetForm = () => {
    setEditingEntryId("");
    setShowQuickTimeOptions(true);
    setShowExactTimeInput(false);
    setFormState(getDefaultFormState());
    setFormErrors({});
    setAttachedImages([]);
    fileUploadRef.current?.clear();
  };

  const scrollComposerToTop = () => {
    window.setTimeout(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const startEditingEntry = (entry: any) => {
    updateComposerVisibility(true);
    setEditingEntryId(String(entry?.entryLogId || ""));
    setShowQuickTimeOptions(false);
    setShowExactTimeInput(true);
    setFormErrors({});
    setFormState({
      title: String(entry?.title || ""),
      occurredAt: formatDateTimeLocalInput(entry?.occurredAt),
      incidentType: String(entry?.movementType || entry?.incidentType || ""),
      details: String(entry?.details || ""),
      actionTaken: String(entry?.actionTaken || ""),
      notificationsMade: String(entry?.notificationsMade || ""),
      notes: String(entry?.notes || ""),
      shiftLabel: String(entry?.shiftLabel || ""),
      tagsInput: Array.isArray(entry?.tags) ? entry.tags.join(", ") : String(entry?.tags || ""),
      involvedResidentIds: Array.isArray(entry?.involvedResidentIds)
        ? entry.involvedResidentIds.map((residentId: any) => String(residentId || "").trim()).filter(Boolean)
        : [],
    });
    setAttachedImages(
      (Array.isArray(entry?.imagePreviews) ? entry.imagePreviews : [])
        .slice(0, MAX_INCIDENT_IMAGES)
        .map((image: any) => ({
          key: String(image?.key || "").trim(),
          fileName: String(image?.fileName || "Image").trim(),
          url: String(image?.url || "").trim(),
        }))
        .filter((image: AttachedImage) => Boolean(image.url)),
    );
    scrollComposerToTop();
  };

  const reuseRecentEntry = (entry: any) => {
    updateComposerVisibility(true);
    setEditingEntryId("");
    setShowQuickTimeOptions(true);
    setShowExactTimeInput(false);
    setFormErrors({});
    setAttachedImages([]);
    fileUploadRef.current?.clear();
    setFormState({
      title: String(entry?.title || "").trim() || "Progress Note",
      occurredAt: formatDateTimeLocalInput(),
      incidentType: "",
      details: String(entry?.details || "").trim(),
      actionTaken: "",
      notificationsMade: "",
      notes: String(entry?.notes || "").trim(),
      shiftLabel: String(entry?.shiftLabel || "").trim(),
      tagsInput: Array.isArray(entry?.tags) ? entry.tags.join(", ") : String(entry?.tags || "").trim(),
      involvedResidentIds: [],
    });
    setShowRecentReuseDialog(false);
    scrollComposerToTop();
  };

  const handleTextFieldChange = (field: keyof FormState, nextValue: string | string[]) => {
    setFormErrors((previousState) => {
      if (!previousState[field]) {
        return previousState;
      }
      return {
        ...previousState,
        [field]: "",
      };
    });

    if (["details", "actionTaken", "notificationsMade", "notes"].includes(field)) {
      setFormState((previousState) => ({
        ...previousState,
        [field]: trimToWordLimit(String(nextValue || ""), AI_POLISH_MAX_WORDS),
      }));
      return;
    }

    setFormState((previousState) => ({
      ...previousState,
      [field]: field === "involvedResidentIds"
        ? (Array.isArray(nextValue) ? nextValue : [])
        : String(nextValue || ""),
    }));
  };

  const buildPayload = (status: "draft" | "submitted" | "final") => ({
    title: entryType === "daily_note"
      ? (formState.title.trim() || "Progress Note")
      : formState.title.trim(),
    occurredAt: toIsoString(formState.occurredAt),
    incidentType: formState.incidentType.trim(),
    movementType: entryType === "resident_movement" ? formState.incidentType.trim() : "",
    details: formState.details.trim(),
    actionTaken: formState.actionTaken.trim(),
    notificationsMade: formState.notificationsMade.trim(),
    notes: formState.notes.trim(),
    shiftLabel: formState.shiftLabel.trim(),
    tags: formState.tagsInput
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    involvedResidentIds: formState.involvedResidentIds,
    involvedResidents: sameFacilityResidentOptions
      .filter((resident) => formState.involvedResidentIds.includes(String(resident?.residentId || "").trim()))
      .map((resident) => ({
        residentId: String(resident?.residentId || "").trim(),
        residentName: String(resident?.residentName || "").trim() || "Resident",
      })),
    status,
  });

  const validateForm = (targetStatus: "draft" | "submitted" | "final") => {
    const nextErrors: FormErrors = {};
    const requiresTimeValidation = targetStatus !== "draft";

    if (entryType === "incident" && !String(formState.title || "").trim()) {
      nextErrors.title = "Title is required.";
    }
    if (requiresTimeValidation && (!String(formState.occurredAt || "").trim() || !toIsoString(formState.occurredAt))) {
      nextErrors.occurredAt = "Time is required.";
    }
    if (entryType !== "resident_movement" && !String(formState.details || "").trim()) {
      nextErrors.details = entryType === "incident" ? "What happened is required." : "Progress note is required.";
    }
    if (entryType === "incident" && !String(formState.incidentType || "").trim()) {
      nextErrors.incidentType = "Incident type is required.";
    }
    if (entryType === "resident_movement" && !String(formState.incidentType || "").trim()) {
      nextErrors.incidentType = "Entry type is required.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getFieldError = (field: keyof FormState) => String(formErrors[field] || "").trim();
  const getInvalidClassName = (field: keyof FormState, className = "") =>
    `${className} ${getFieldError(field) ? "p-invalid" : ""}`.trim();

  const submitForm = async (targetStatus: "draft" | "submitted" | "final") => {
    if (!validateForm(targetStatus)) {
      scrollComposerToTop();
      return;
    }

    setSubmittingForm(true);
    try {
      let uploadedImages = attachedImages;
      const newFiles = attachedImages.filter((image) => image.file && !image.key).map((image) => image.file!) as File[];
      if (newFiles.length && onUploadImages) {
        const uploadedResults = await onUploadImages(newFiles);
        const existingImages = attachedImages.filter((image) => image.key);
        uploadedImages = [
          ...existingImages,
          ...uploadedResults.map((image) => ({
            key: image.key,
            fileName: image.fileName,
            url: image.url,
          })),
        ].slice(0, MAX_INCIDENT_IMAGES);
        setAttachedImages(uploadedImages);
      }

      const payload = {
        ...buildPayload(targetStatus),
        imageKeys: uploadedImages.map((image) => String(image.key || "").trim()).filter(Boolean),
        imageFileNames: uploadedImages.map((image) => String(image.fileName || "").trim()).filter(Boolean),
      };

      if (editingEntryId) {
        await onUpdateEntry(editingEntryId, { entryLogId: editingEntryId, ...payload });
      } else {
        await onCreateEntry(payload);
      }

      resetForm();
      if (targetStatus === "draft") {
        onDraftSaved?.();
        return;
      }
      if (showComposerToggleButton || isComposerControlled) {
        updateComposerVisibility(false);
      }
    } finally {
      setSubmittingForm(false);
    }
  };

  const updateOccurredAt = (nextValue: string) => {
    setFormErrors((previousState) => {
      if (!previousState.occurredAt) {
        return previousState;
      }
      return {
        ...previousState,
        occurredAt: "",
      };
    });
    setFormState((previousState) => ({
      ...previousState,
      occurredAt: nextValue,
    }));
  };

  const updateOccurredAtFromInterval = (minutesAgo: number) => {
    const nextValue = formatDateTimeLocalInput(new Date(Date.now() - minutesAgo * 60000).toISOString());
    updateOccurredAt(nextValue);
    setShowQuickTimeOptions(false);
  };

  const selectedQuickMinutes = useMemo(() => {
    const occurredAtDate = new Date(toIsoString(formState.occurredAt));
    if (Number.isNaN(occurredAtDate.getTime())) {
      return null;
    }
    const minutesAgo = Math.round((Date.now() - occurredAtDate.getTime()) / 60000);
    return QUICK_TIME_MINUTES.find((minutes) => Math.abs(minutesAgo - minutes) <= QUICK_TIME_MATCH_TOLERANCE_MINUTES) ?? null;
  }, [formState.occurredAt]);

  const handleDeleteDraft = async (entry: any) => {
    if (!onDeleteDraft) {
      return;
    }

    const isDraft = String(entry?.status || "").trim().toLowerCase() === "draft";

    confirmDialog({
      header: isDraft ? "Delete Draft" : "Delete Progress Note",
      message: renderConfirmMessage(
        isDraft ? "Delete this draft?" : "Delete this progress note?",
        isDraft
          ? "This saved draft will be removed and cannot be recovered."
          : "This progress note will be removed and cannot be recovered.",
      ),
      acceptLabel: isDraft ? "Delete Draft" : "Delete Note",
      rejectLabel: isDraft ? "Keep Draft" : "Keep Note",
      ...appConfirmDialogClasses,
      accept: () => {
        void onDeleteDraft(String(entry?.entryLogId || ""));
      },
    });
  };

  const handleImageSelect = (event: FileUploadSelectEvent) => {
    const selectedFiles = (event.files || []) as File[];
    if (!selectedFiles.length) {
      return;
    }

    setAttachedImages((previousState) => {
      const remainingSlots = MAX_INCIDENT_IMAGES - previousState.length;
      const filesToAdd = selectedFiles.slice(0, Math.max(remainingSlots, 0));
      const nextImages = [
        ...previousState,
        ...filesToAdd.map((file) => ({
          file,
          fileName: file.name,
          url: URL.createObjectURL(file),
        })),
      ];
      return nextImages.slice(0, MAX_INCIDENT_IMAGES);
    });

    fileUploadRef.current?.clear();
  };

  const removeAttachedImage = (indexToRemove: number) => {
    setAttachedImages((previousState) => previousState.filter((_image, index) => index !== indexToRemove));
  };

  const generateDailyNote = async () => {
    if (entryType !== "daily_note" || !onFormatText || !String(formState.details || "").trim()) {
      return;
    }

    setGeneratingDailyNote(true);
    try {
      const response = await onFormatText({
        text: String(formState.details).trim(),
        taskType: "professional-note-cleanup",
        tone: "professional",
        domain: "daily-note-autofill",
        outputStyle: "plain_text",
      });

      const formattedText = String(response?.formattedText || "").trim();
      const titleMatch = formattedText.match(/Title:\s*(.+)/i);
      const shiftMatch = formattedText.match(/Shift:\s*(.+)/i);
      const detailsMatch = formattedText.match(/Progress Note:\s*([\s\S]*?)(?:\nExtra Notes:|$)/i);
      const notesMatch = formattedText.match(/Extra Notes:\s*([\s\S]*)$/i);

      const parsedShift = String(shiftMatch?.[1] || "")
        .trim()
        .toLowerCase();
      const normalizedShift = ["day", "evening", "night", "other"].includes(parsedShift) ? parsedShift : "";
      const parsedDetails = String(detailsMatch?.[1] || formattedText).trim();

      setFormState((previousState) => ({
        ...previousState,
        title: String(titleMatch?.[1] || "").trim() || previousState.title || "Progress Note",
        shiftLabel: normalizedShift || previousState.shiftLabel,
        details: parsedDetails,
        notes: String(notesMatch?.[1] || "").trim(),
      }));
    } finally {
      setGeneratingDailyNote(false);
    }
  };

  const generateIncidentSample = async () => {
    if (entryType !== "incident" || !onFormatText || !String(formState.incidentType || "").trim()) {
      return;
    }

    setGeneratingIncidentSample(true);
    try {
      const selectedResidentNames = sameFacilityResidentOptions
        .filter((resident) => formState.involvedResidentIds.includes(String(resident?.residentId || "").trim()))
        .map((resident) => String(resident?.residentName || "").trim())
        .filter(Boolean);
      const incidentTypeLabel = INCIDENT_TYPE_OPTIONS.find((option) => option.value === formState.incidentType)?.label || "Incident";
      const response = await onFormatText({
        taskType: "professional-note-cleanup",
        tone: "professional",
        domain: "incident-report-sample",
        outputStyle: "plain_text",
        metadata: {
          incidentType: String(formState.incidentType || "").trim(),
          occurredAt: String(formState.occurredAt || "").trim(),
          involvedResidentNames: selectedResidentNames,
        },
      });

      const formattedText = String(response?.formattedText || "").trim();
      const titleMatch = formattedText.match(/Title:\s*(.+)/i);
      const detailsMatch = formattedText.match(/What Happened:\s*([\s\S]*?)(?:\nAction Taken:|$)/i);
      const actionTakenMatch = formattedText.match(/Action Taken:\s*([\s\S]*?)(?:\nWho Was Notified:|$)/i);
      const notificationsMatch = formattedText.match(/Who Was Notified:\s*([\s\S]*?)(?:\nExtra Notes:|$)/i);
      const notesMatch = formattedText.match(/Extra Notes:\s*([\s\S]*)$/i);

      setFormState((previousState) => ({
        ...previousState,
        title: String(titleMatch?.[1] || "").trim() || previousState.title || incidentTypeLabel,
        details: String(detailsMatch?.[1] || "").trim() || previousState.details,
        actionTaken: String(actionTakenMatch?.[1] || "").trim() || previousState.actionTaken,
        notificationsMade: String(notificationsMatch?.[1] || "").trim() || previousState.notificationsMade,
        notes: String(notesMatch?.[1] || "").trim() || previousState.notes,
      }));
    } finally {
      setGeneratingIncidentSample(false);
    }
  };

  const formatFieldWithAi = async (field: "details" | "actionTaken" | "notificationsMade" | "notes") => {
    if (!onFormatText) {
      return;
    }

    const sourceText = String(formState[field] || "").trim();
    if (!sourceText || countWords(sourceText) > AI_POLISH_MAX_WORDS) {
      return;
    }

    setFormattingField(field);
    try {
      const response = await onFormatText({
        text: sourceText,
        taskType: "professional-note-cleanup",
        tone: "professional",
        domain: entryType === "incident" ? `incident-report-${field}` : `daily-note-${field}`,
        outputStyle: "plain_text",
      });
      const formattedText = String(response?.formattedText || "").trim();
      if (formattedText) {
        handleTextFieldChange(field, formattedText);
      }
    } finally {
      setFormattingField("");
    }
  };

  const renderAiButton = (field: "details" | "actionTaken" | "notificationsMade" | "notes") => {
    if (!onFormatText || entryType === "daily_note") {
      return null;
    }

    const fieldValue = String(formState[field] || "");
    const wordCount = countWords(fieldValue);
    return (
      <Button
        type="button"
        label={formattingField === field ? "Improving..." : "Fix with AI"}
        icon="pi pi-sparkles"
        severity="warning"
        text
        disabled={!fieldValue.trim() || wordCount > AI_POLISH_MAX_WORDS || formattingField === field}
        onClick={() => { void formatFieldWithAi(field); }}
      />
    );
  };

  const renderTextField = (
    field: "details" | "actionTaken" | "notificationsMade" | "notes",
    label: string,
    placeholder: string,
    rows = 4,
  ) => {
    const wordCount = countWords(formState[field] || "");
    const showDailyNoteGenerator = entryType === "daily_note" && field === "details";
    return (
      <div>
        <div className="flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
          <label style={{ fontWeight: 700, color: "#0f172a" }}>{label}</label>
          <div className="flex align-items-center gap-2">
            <span style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>{wordCount}/{AI_POLISH_MAX_WORDS} words</span>
            {showDailyNoteGenerator ? (
              <Button
                type="button"
                label={generatingDailyNote ? "Improving..." : "Fix with AI"}
                icon="pi pi-sparkles"
                severity="warning"
                rounded
                outlined
                disabled={!String(formState.details || "").trim() || generatingDailyNote}
                loading={generatingDailyNote}
                onClick={() => { void generateDailyNote(); }}
              />
            ) : renderAiButton(field)}
          </div>
        </div>
        <InputTextarea
          autoResize
          rows={rows}
          value={formState[field]}
          placeholder={placeholder}
          onChange={(e) => handleTextFieldChange(field, e.target.value)}
          className={getInvalidClassName(field)}
          style={{ width: "100%" }}
        />
        {getFieldError(field) ? (
          <div className="mt-2" style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
            {getFieldError(field)}
          </div>
        ) : null}
      </div>
    );
  };


  const renderEntryCard = (entry: any) => {
    const canEdit = canEditEntry ? canEditEntry(entry) : true;
    const isDraft = String(entry?.status || "").trim().toLowerCase() === "draft";
    const canDelete = Boolean(onDeleteDraft) && (isDraft || (entryType === "daily_note" && canEdit));
    const tagText = isDraft ? "Draft" : (entryType === "incident" ? "Submitted" : "Saved");
    const recordedByRole = String(entry?.recordedByRole || "").trim().toLowerCase();
    const showDailyNoteExtraNotes = !(entryType === "daily_note" && recordedByRole && recordedByRole !== "provider");

    return (
      <div
        key={String(entry?.entryLogId || "")}
        style={{
          ...appInnerCardStyle,
          padding: "1rem",
          boxShadow: "none",
        }}
      >
        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: "#0f172a" }}>
              {String(entry?.title || "").trim() || (entryType === "incident" ? "Incident Report" : "Progress Note")}
            </div>
            <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
              {formatTimestamp(entry?.occurredAt)}
              {getResidentEntryDisplayStaffName(entry) ? ` • By ${getResidentEntryDisplayStaffName(entry)}` : ""}
              {entry?.shiftLabel ? ` • Shift ${entry.shiftLabel}` : ""}
            </div>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "999px",
              padding: "0.24rem 0.7rem",
              background: isDraft ? "#fff7ed" : "#eff6ff",
              border: isDraft ? "1px solid #fdba74" : "1px solid #bfdbfe",
              color: isDraft ? "#9a3412" : "#185DA2",
              fontWeight: 700,
              fontSize: "0.78rem",
            }}
          >
            {tagText}
          </span>
        </div>
        {entry?.incidentType ? (
          <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            Type: <strong style={{ color: "#334155" }}>{String(entry.incidentType).replace(/_/g, " ")}</strong>
          </div>
        ) : null}
        {Array.isArray(entry?.involvedResidents) && entry.involvedResidents.length ? (
          <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Other residents involved:</strong>{" "}
            {entry.involvedResidents.map((resident: any) => String(resident?.residentName || "").trim()).filter(Boolean).join(", ")}
          </div>
        ) : null}
        {entry?.details ? (
          <div className="mt-3" style={{ whiteSpace: "pre-wrap", color: "#0f172a", lineHeight: 1.6 }}>
            {entry.details}
          </div>
        ) : null}
        {entry?.actionTaken ? (
          <div className="mt-3" style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
            <strong style={{ color: "#334155" }}>Action taken:</strong> {entry.actionTaken}
          </div>
        ) : null}
        {entry?.notificationsMade ? (
          <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
            <strong style={{ color: "#334155" }}>Who was notified:</strong> {entry.notificationsMade}
          </div>
        ) : null}
        {entry?.notes && showDailyNoteExtraNotes ? (
          <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
            <strong style={{ color: "#334155" }}>Extra notes:</strong> {entry.notes}
          </div>
        ) : null}
        {Array.isArray(entry?.imagePreviews) && entry.imagePreviews.length ? (
          <div className="mt-3">
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.55rem" }}>Photos</div>
            <div className="flex gap-2 flex-wrap">
              {entry.imagePreviews.map((image: any, imageIndex: number) => (
                <button
                  type="button"
                  key={`${entry?.entryLogId || "entry"}-image-${imageIndex}`}
                  onClick={() => setPreviewImage({
                    key: String(image?.key || "").trim(),
                    fileName: String(image?.fileName || `Incident image ${imageIndex + 1}`).trim(),
                    url: String(image?.url || "").trim(),
                  })}
                  style={{
                    display: "inline-flex",
                    flexDirection: "column",
                    gap: "0.35rem",
                    textDecoration: "none",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  aria-label={`Preview ${String(image?.fileName || `incident image ${imageIndex + 1}`)}`}
                >
                  <img
                    src={String(image?.url || "").trim()}
                    alt={String(image?.fileName || `Incident image ${imageIndex + 1}`)}
                    style={{
                      width: "88px",
                      height: "88px",
                      objectFit: "cover",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mt-3 flex justify-content-end gap-2 flex-wrap">
          {!readOnly && !isDraft && entryType === "daily_note" ? (
            <Button
              type="button"
              label="Copy to New Entry"
              severity="warning"
              rounded
              outlined
              onClick={() => reuseRecentEntry(entry)}
            />
          ) : null}
          {canDelete ? (
            <Button
              type="button"
              label={isDraft ? "Delete Draft" : "Delete"}
              severity="danger"
              rounded
              outlined
              onClick={() => { void handleDeleteDraft(entry); }}
            />
          ) : null}
          {canEdit ? (
            <Button
              type="button"
              label={isDraft ? "Resume Draft" : "Edit"}
              severity="warning"
              rounded
              outlined
              onClick={() => startEditingEntry(entry)}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    if (!onEntriesPreviousPage && !onEntriesNextPage) {
      return null;
    }
    return (
      <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
        <Button
          type="button"
          label="Previous"
          severity="warning"
          rounded
          outlined
          disabled={!serverEntriesHasPreviousPage}
          onClick={onEntriesPreviousPage}
        />
        <div style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
          Page {serverEntriesPage}
        </div>
        <Button
          type="button"
          label="Next"
          severity="warning"
          rounded
          outlined
          disabled={!serverEntriesHasNextPage}
          onClick={onEntriesNextPage}
        />
      </div>
    );
  };

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: showRecentReuseSummaryCard ? "68rem" : "54rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        {showComposerToggleButton ? (
          <div className="flex justify-content-center">
            <Button
              type="button"
              label={composerIsVisible ? labels.hideButtonLabel : labels.newButtonLabel}
              severity="warning"
              rounded
              outlined
              onClick={() => updateComposerVisibility(!composerIsVisible)}
            />
          </div>
        ) : null}

        {composerIsVisible ? (
          <div className="grid align-items-start">
            {showRecentReuseSummaryCard ? (
              <div className="col-12 lg:col-4">
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    padding: "1rem",
                    background: "#f8fafc",
                    display: "grid",
                    gap: "0.9rem",
                    position: "sticky",
                    top: "1rem",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800, color: "#0f172a" }}>Quick Reuse From Last 10 Entries</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.84rem", marginTop: "0.35rem" }}>
                      Reuse a recent daily note to start faster, then adjust it for this shift.
                    </div>
                  </div>
                  <div style={{ ...appInnerCardStyle, padding: "0.8rem 0.9rem", boxShadow: "none" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{recentReuseEntries.length} recent reusable entries</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.25rem" }}>
                      Last note: {formatTimestamp(recentReuseEntries[0]?.occurredAt)}
                    </div>
                  </div>
                  {allowDrafts ? <Button
                    type="button"
                    label="Reuse Recent Entries"
                    icon="pi pi-history"
                    severity="warning"
                    rounded
                    outlined
                    onClick={() => setShowRecentReuseDialog(true)}
                  /> : null}
                </div>
              </div>
            ) : null}
            <div className={`col-12 ${showRecentReuseSummaryCard ? "lg:col-8" : ""}`}>
              <div ref={composerRef} style={appInnerCardStyle}>
                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem" }}>
                  {editingEntryId ? `Update ${labels.entryLabel}` : labels.newButtonLabel}
                </div>
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.9rem" }}>
                  Keep the note clear and simple. You can save a draft now and finish it later.
                </div>

                <div className="mt-4" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div className="grid">
                    <div className={`col-12 ${entryType === "incident" ? "" : ""}`}>
                      <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
                        <label style={{ fontWeight: 700, color: "#0f172a" }}>Time</label>
                        <div className="flex gap-2 flex-wrap">
                          {entryType === "daily_note" && !showExactTimeInput ? QUICK_TIME_MINUTES.map((minutesAgo) => {
                            const isSelected = selectedQuickMinutes === minutesAgo;
                            return (
                              <Button
                                key={minutesAgo}
                                type="button"
                                label={getQuickTimeLabel(minutesAgo)}
                                rounded
                                outlined={!isSelected}
                                severity="warning"
                                onClick={() => updateOccurredAtFromInterval(minutesAgo)}
                              />
                            );
                          }) : null}
                          <Button
                            type="button"
                            label={showExactTimeInput ? "Hide Exact Time" : "Exact Date and Time"}
                            severity="warning"
                            text
                            onClick={() => setShowExactTimeInput((previousState) => !previousState)}
                          />
                          {!showQuickTimeOptions ? (
                            <Button
                              type="button"
                              label="Reset Time"
                              severity="warning"
                              text
                              onClick={() => {
                                setShowQuickTimeOptions(true);
                                updateOccurredAt(formatDateTimeLocalInput());
                              }}
                            />
                          ) : null}
                        </div>
                      </div>
                      {entryType === "incident" ? (
                        <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                          {formState.occurredAt ? `Selected time: ${formatTimestamp(toIsoString(formState.occurredAt) || formState.occurredAt)}` : "Pick when this happened."}
                        </div>
                      ) : null}
                      {entryType === "incident" && showQuickTimeOptions ? (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {QUICK_TIME_MINUTES.map((minutesAgo) => {
                            const isSelected = selectedQuickMinutes === minutesAgo;
                            return (
                              <Button
                                key={minutesAgo}
                                type="button"
                                label={getQuickTimeLabel(minutesAgo)}
                                rounded
                                outlined={!isSelected}
                                severity="warning"
                                onClick={() => updateOccurredAtFromInterval(minutesAgo)}
                              />
                            );
                          })}
                        </div>
                      ) : null}
                      {showExactTimeInput ? (
                        <div className="grid mt-3">
                          <div className="col-12 md:col-6">
                            <Calendar
                              value={getLosAngelesCalendarValue(getLosAngelesDateKey(formState.occurredAt))}
                              onChange={(event) => {
                                const nextDateKey = getLosAngelesDateKeyFromCalendarValue(event.value as Date | Date[] | null);
                                if (nextDateKey) {
                                  updateOccurredAt(updateLosAngelesDateTimePart(formState.occurredAt, nextDateKey));
                                  setShowQuickTimeOptions(false);
                                }
                              }}
                              dateFormat="mm/dd/yy"
                              showIcon
                              placeholder="Date"
                              className={getInvalidClassName("occurredAt", "w-full")}
                              inputClassName="w-full"
                            />
                          </div>
                          <div className="col-12 md:col-6">
                            <Dropdown
                              value={getLosAngelesTimeKey(formState.occurredAt)}
                              options={LOS_ANGELES_TIME_OPTIONS}
                              onChange={(event) => {
                                updateOccurredAt(updateLosAngelesDateTimePart(
                                  formState.occurredAt,
                                  getLosAngelesDateKey(formState.occurredAt),
                                  String(event.value || ""),
                                ));
                                setShowQuickTimeOptions(false);
                              }}
                              placeholder="Time"
                              className={getInvalidClassName("occurredAt", "w-full")}
                            />
                          </div>
                        </div>
                      ) : null}
                      {getFieldError("occurredAt") ? (
                        <div className="mt-2" style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                          {getFieldError("occurredAt")}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {entryType === "incident" ? (
                    <div className="grid">
                      <div className={`col-12 ${sameFacilityResidentOptions.length ? "md:col-6" : ""}`}>
                        <label style={{ fontWeight: 700, color: "#0f172a" }}>Incident type</label>
                        <Dropdown
                          value={formState.incidentType}
                          options={INCIDENT_TYPE_OPTIONS}
                          onChange={(e) => handleTextFieldChange("incidentType", e.value || "")}
                          filter
                          showClear
                          placeholder="Search and select incident type"
                          className={getInvalidClassName("incidentType", "w-full mt-2")}
                        />
                        {getFieldError("incidentType") ? (
                          <div className="mt-2" style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                            {getFieldError("incidentType")}
                          </div>
                        ) : null}
                        <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                          Pick a type, then use `Auto-Generate Content` below to draft the other incident fields for quick editing.
                        </div>
                      </div>
                      {sameFacilityResidentOptions.length ? (
                        <div className="col-12 md:col-6">
                          <label style={{ fontWeight: 700, color: "#0f172a" }}>Other residents involved</label>
                          <MultiSelect
                            value={formState.involvedResidentIds}
                            options={sameFacilityResidentOptions.map((resident) => ({
                              label: resident.residentName,
                              value: resident.residentId,
                            }))}
                            onChange={(e) => handleTextFieldChange("involvedResidentIds", Array.isArray(e.value) ? e.value : [])}
                            display="chip"
                            filter
                            maxSelectedLabels={3}
                            placeholder="Select other residents from this facility"
                            className="w-full mt-2"
                          />
                          <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                            Optional. Use this when the incident involved more than one resident.
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {entryType === "resident_movement" ? (
                    <div>
                      <label style={{ fontWeight: 700, color: "#0f172a" }}>Entry type</label>
                      <Dropdown
                        value={formState.incidentType}
                        options={MOVEMENT_TYPE_OPTIONS}
                        onChange={(event) => handleTextFieldChange("incidentType", event.value || "")}
                        className={getInvalidClassName("incidentType", "w-full mt-2")}
                      />
                      {getFieldError("incidentType") ? <div className="mt-2" style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>{getFieldError("incidentType")}</div> : null}
                    </div>
                  ) : null}

                  {entryType === "incident" && onFormatText ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        type="button"
                        label={generatingIncidentSample ? "Generating..." : "Auto-Generate Content"}
                        icon="pi pi-sparkles"
                        severity="warning"
                        rounded
                        disabled={!String(formState.incidentType || "").trim() || generatingIncidentSample}
                        loading={generatingIncidentSample}
                        onClick={() => { void generateIncidentSample(); }}
                      />
                    </div>
                  ) : null}

                  {entryType === "incident" ? (
                    <div>
                      <label style={{ fontWeight: 700, color: "#0f172a" }}>Title</label>
                      <InputText
                        value={formState.title}
                        onChange={(e) => handleTextFieldChange("title", e.target.value)}
                        placeholder="Short incident title"
                        className={getInvalidClassName("title", "w-full mt-2")}
                      />
                      {getFieldError("title") ? (
                        <div className="mt-2" style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                          {getFieldError("title")}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {renderTextField("details", labels.detailsLabel, labels.detailsPlaceholder, 5)}
                  {entryType === "incident" ? renderTextField("actionTaken", "Support or action taken", "Write the support given or action taken.", 4) : null}
                  {entryType === "incident" ? renderTextField("notificationsMade", "Who was notified", "Write who was updated or contacted.", 3) : null}
                  {entryType === "incident" ? renderTextField("notes", labels.notesLabel, "Anything else that would help the next person reading this note.", 3) : null}

                  {enableImages ? (
                    <div>
                      <div className="flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
                        <label style={{ fontWeight: 700, color: "#0f172a" }}>Photos</label>
                        <span style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                          Up to {MAX_INCIDENT_IMAGES} images
                        </span>
                      </div>
                      <FileUpload
                        ref={fileUploadRef}
                        mode="basic"
                        name="incidentImages"
                        accept="image/*"
                        multiple
                        auto={false}
                        customUpload
                        chooseLabel="Add Photos"
                        chooseOptions={{
                          className: "p-button-warning p-button-outlined p-button-rounded",
                        }}
                        onSelect={handleImageSelect}
                        disabled={attachedImages.length >= MAX_INCIDENT_IMAGES || saving}
                      />
                      {attachedImages.length ? (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {attachedImages.map((image, imageIndex) => (
                            <div
                              key={`${image.key || image.fileName}-${imageIndex}`}
                              style={{
                                width: "98px",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                padding: "0.35rem",
                                background: "#ffffff",
                              }}
                            >
                              <img
                                src={image.url}
                                alt={image.fileName}
                                style={{
                                  width: "100%",
                                  height: "72px",
                                  objectFit: "cover",
                                  borderRadius: "9px",
                                }}
                              />
                              <div
                                className="mt-2"
                                style={{
                                  fontSize: "0.72rem",
                                  color: "#475569",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={image.fileName}
                              >
                                {image.fileName}
                              </div>
                              <Button
                                type="button"
                                label="Remove"
                                severity="danger"
                                text
                                size="small"
                                className="mt-1 p-0"
                                onClick={() => removeAttachedImage(imageIndex)}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                          Add clear photos if they help explain what happened.
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="mt-4 flex justify-content-end gap-2 flex-wrap">
                  {editingEntryId ? (
                    <Button
                      type="button"
                      label="Cancel"
                      severity="secondary"
                      rounded
                      outlined
                      onClick={resetForm}
                    />
                  ) : null}
                  {allowDrafts ? <Button
                    type="button"
                    label={labels.saveDraftLabel}
                    severity="warning"
                    rounded
                    outlined
                    loading={saving || submittingForm}
                    onClick={() => { void submitForm("draft"); }}
                    disabled={saving || submittingForm}
                  /> : null}
                  <Button
                    type="button"
                    label={labels.saveFinalLabel}
                    severity="warning"
                    rounded
                    loading={saving || submittingForm}
                    onClick={() => { void submitForm(labels.statusFinalLabel as "submitted" | "final"); }}
                    disabled={saving || submittingForm}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {showDraftsSection ? (
          <div style={appInnerCardStyle}>
            <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                {labels.draftHeader}
                <span style={{ ...appMutedTextStyle, marginLeft: "0.45rem", fontSize: "0.9rem" }}>
                  {draftEntries.length}
                </span>
              </div>
            </div>
            {draftEntries.length ? (
              <div className="mt-3" style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                {draftEntries.map((entry) => renderEntryCard(entry))}
              </div>
            ) : (
              <div className="mt-3" style={appMutedTextStyle}>No saved drafts yet.</div>
            )}
          </div>
        ) : null}

        {!hideEntriesSection && (showEntriesToggleButton || entriesAreVisible) ? (
          <div style={appInnerCardStyle}>
            <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
              <div style={{ fontWeight: 800, color: "#0f172a" }}>{labels.recentHeader}</div>
              {showEntriesToggleButton ? (
                <Button
                  type="button"
                  label={entriesAreVisible ? "Hide" : "Show"}
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => updateEntriesVisibility(!entriesAreVisible)}
                />
              ) : null}
            </div>

            {entriesAreVisible ? (
              <div className="mt-3" style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
                {renderPagination()}
                {loading ? (
                  <div className="flex justify-content-center py-4">
                    <ProgressSpinner style={{ width: "48px", height: "48px" }} strokeWidth="4" />
                  </div>
                ) : recentEntries.length ? (
                  recentEntries.map((entry) => renderEntryCard(entry))
                ) : (
                  <div style={appMutedTextStyle}>No entries yet.</div>
                )}
                {renderPagination()}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <Dialog
        header={previewImage?.fileName || "Incident photo"}
        visible={Boolean(previewImage)}
        onHide={() => setPreviewImage(null)}
        style={{ width: "min(92vw, 56rem)" }}
        contentStyle={{ padding: "0.75rem", textAlign: "center" }}
      >
        {previewImage?.url ? (
          <img
            src={previewImage.url}
            alt={previewImage.fileName || "Incident photo"}
            style={{
              maxWidth: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              borderRadius: "12px",
            }}
          />
        ) : null}
      </Dialog>
      <Dialog
        header="Quick Reuse From Last 10 Entries"
        visible={showRecentReuseDialog}
        onHide={() => setShowRecentReuseDialog(false)}
        style={{ width: "min(96vw, 56rem)" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
          <div style={{ ...appMutedTextStyle, fontSize: "0.9rem" }}>
            Pick a recent daily note to prefill the form. The time resets to now so you can quickly document today&apos;s update.
          </div>
          {recentReuseEntries.length ? recentReuseEntries.map((entry: any) => (
            <div
              key={`reuse-entry-${String(entry?.entryLogId || "")}`}
              style={{
                ...appInnerCardStyle,
                padding: "0.9rem 1rem",
                boxShadow: "none",
              }}
            >
              <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>
                    {String(entry?.title || "").trim() || "Progress Note"}
                  </div>
                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                    {formatTimestamp(entry?.occurredAt)}
                    {entry?.shiftLabel ? ` • Shift ${entry.shiftLabel}` : ""}
                    {getResidentEntryDisplayStaffName(entry) ? ` • By ${getResidentEntryDisplayStaffName(entry)}` : ""}
                  </div>
                  {entry?.details ? (
                    <div className="mt-2" style={{ color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      {String(entry.details).trim().slice(0, 220)}
                      {String(entry.details).trim().length > 220 ? "..." : ""}
                    </div>
                  ) : null}
                </div>
                <Button
                  type="button"
                  label="Reuse"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => reuseRecentEntry(entry)}
                />
              </div>
            </div>
          )) : (
            <div style={appMutedTextStyle}>No recent daily notes are available to reuse yet.</div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default ResidentEntryLogPanel;
